import React, { Fragment } from "react"

import { useTranslation } from "react-i18next"

import { CircularProgress, Grid } from "@mui/material"
import { Snackbar, Typography } from "@synapse-analytics/synapse-ui"

import { LogViewer } from "../../../components/data-display/LogViewer"
import type { PostCreationDialogProps } from "../interfaces"

import Styles from "../Projects.module.scss"

/**
 * Project creation overview, first screen in the creation flow
 * @param  {string} creationLogs model creation logs
 * @param  {Record<string, string>} creationErrors errors if creation fails
 * @param  {boolean} isLoading loading mode
 * @return  {React.ReactElement}
 */
export function PostCreationDialog(props: Readonly<PostCreationDialogProps>): React.ReactElement {
  const { creationLogs, creationErrors, isLoading } = props
  const { t } = useTranslation("projects")

  return (
    <Fragment>
      {isLoading ? (
        <Grid
          container
          justifyContent="center"
          alignContent="center"
          direction="column"
          spacing={2}
          className={Styles.creationDialogRoot}
        >
          {/* isLoading mode */}
          <Grid container item justifyContent="center" alignContent="center">
            <CircularProgress />
          </Grid>

          <Grid container item justifyContent="center" alignContent="center">
            <Typography variant="h3-bold">{t("creationFlow.dialog.creating")}</Typography>
          </Grid>

          <Grid container item justifyContent="center" alignContent="center">
            <Typography variant="h3-regular" textTransform="initial">
              {t("creationFlow.dialog.keepWindowOpen")}
            </Typography>
          </Grid>
        </Grid>
      ) : (
        <Grid container direction="column" spacing={1}>
          <Grid item>
            {/* creation status */}
            {creationErrors && creationErrors.length > 0 ? (
              <Snackbar severity="error" fullWidth description={t("creationFlow.dialog.modelFailed")} />
            ) : (
              <Snackbar severity="success" fullWidth description={t("creationFlow.dialog.modelSuccessful")} />
            )}
          </Grid>

          <Grid item>
            <Typography variant="h3-bold" gutterBottom>
              {t("creationFlow.dialog.modelLogs")}
            </Typography>

            {creationLogs ? (
              <LogViewer data={creationLogs.trim().split("\n")} isLoading={isLoading} />
            ) : (
              <Snackbar severity="info" fullWidth description={t("creationFlow.dialog.noLogsToDisplay")} />
            )}
          </Grid>
        </Grid>
      )}
    </Fragment>
  )
}
